import { useState } from 'react'
import { Download, FileText, Sparkles, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const DISMISSED_KEY = 'docfiller:onboarding-dismissed-v1'

interface OnboardingBannerProps {
  className?: string
}

export function OnboardingBanner({ className }: OnboardingBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // localStorage unavailable (private browsing, storage quota) — just
      // hide for this session without persisting.
    }
    setDismissed(true)
  }

  if (dismissed) return null

  return (
    <div
      role="region"
      aria-label="Getting started with Doc Filler"
      data-slot="onboarding-banner"
      className={cn(
        'relative rounded-lg border border-primary/20 bg-primary-soft/30 px-4 py-4 sm:px-5',
        className,
      )}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={dismiss}
        aria-label="Dismiss getting started tips"
        className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
      >
        <X className="size-4" aria-hidden />
      </Button>

      <div className="pr-8">
        <p className="mb-3 text-sm font-semibold text-foreground">
          Welcome — here&apos;s how Doc Filler works
        </p>
        <ol className="space-y-2.5" data-testid="onboarding-steps">
          {STEPS.map(({ Icon, label, detail }, i) => (
            <li key={label} className="flex items-start gap-2.5">
              <span
                aria-hidden
                className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground"
              >
                {i + 1}
              </span>
              <Icon
                className="mt-0.5 size-4 shrink-0 text-primary"
                aria-hidden
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                <span className="font-medium text-foreground">{label}</span>
                {' — '}
                {detail}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

const STEPS = [
  {
    Icon: FileText,
    label: 'Upload a document',
    detail: 'Drop a PDF or DOCX below (up to 20 MB).',
  },
  {
    Icon: Sparkles,
    label: 'Review AI-filled fields',
    detail:
      'The agent extracts every form field and prefills values from your saved context. Accept, edit, or dismiss each suggestion.',
  },
  {
    Icon: Download,
    label: 'Download the completed doc',
    detail:
      'Click Download to render the finished document as PDF or DOCX and save it to your device.',
  },
]
